import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Row, Select } from 'antd';
import { FormInput } from 'components/Custom/inputs';
import { FormSelect } from 'components/Custom/selects';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { User } from 'store/auth/models';
import { CountryFlag, Timezone } from 'store/common/models';
import { renderPhoneCodeOption } from 'utils/render-countries-codes';

interface ContactDetailsFormProps {
  updateContact: (user: User) => void;
  form: any;
  timezones: Timezone[];
  countriesExternalData: CountryFlag[];
}

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  updateContact,
  form,
  timezones,
  countriesExternalData,
}) => {
  const { t } = useTranslation();
  const { language } = useCountryLabel();
  const handleSaveDetails = (values) => {
    const user = values as User;
    updateContact(user);
  };
  return (
    <Form layout="vertical" onFinish={handleSaveDetails} form={form}>
      <Row gutter={24} justify={'space-between'} align={'bottom'}>
        <Col span={24}>
          <Form.Item
            name="firstName"
            label={t('signup.first-name')}
            rules={[
              {
                required: true,
                message: t('common.required-field'),
              },
            ]}
          >
            <FormInput
              width="100%" //@ts-ignore
              border_color="var(--color-border, #dbeae3)"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="lastName"
            label={t('signup.last-name')}
            rules={[
              {
                required: true,
                message: t('common.required-field'),
              },
            ]}
          >
            <FormInput
              width="100%" //@ts-ignore
              border_color="var(--color-border, #dbeae3)"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="companyName"
            label={t('signup.company-name')}
            rules={[
              {
                required: true,
                message: t('common.required-field'),
              },
            ]}
          >
            <FormInput
              width="100%" //@ts-ignore
              border_color="var(--color-border, #dbeae3)"
            />
          </Form.Item>
        </Col>
        <Col span={24} style={{ display: 'flex' }}>
          <Form.Item
            name="phonePrefix"
            label={t('signup.code')}
            rules={[{ required: true, message: t('common.required-field') }]}
            style={{
              width: '120px',
              minWidth: '120px',
              maxWidth: '120px',
              marginRight: '2%',
            }}
          >
            <FormSelect //@ts-ignore
              width={'100%'}
              height="45px"
              placeholder={t('signup.code')}
              // Keep the phone-code dropdown wider than the compact trigger input
              // so country names and dialing codes remain visible.
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 330 }}
              optionLabelProp="value"
              id="counties-code"
              bgcolor="var(--color-surface-raised, #FFFFFF)"
              showSearch={true}
              filterOption={(input, option) => {
                const inputLower = input.toLowerCase();
                const valueLower = option?.value?.toLowerCase() || '';
                const labelLower = option?.label?.toLowerCase() || '';
                return (
                  valueLower.includes(inputLower) ||
                  labelLower.includes(inputLower)
                );
              }}
            >
              {countriesExternalData &&
                countriesExternalData.map((c) => renderPhoneCodeOption(c, language))}
            </FormSelect>
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label={t('contacts.phone')}
            rules={[{ required: true, message: t('common.required-field') }]}
            style={{ flex: 1 }}
          >
            <FormInput width={'100%'} placeholder={t('contacts.phone')} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="website" label={t('common.website')}>
            <FormInput
              width="100%" //@ts-ignore
              border_color="var(--color-border, #dbeae3)"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="email"
            label={t('signup.email-address')}
            rules={[
              {
                required: true,
                message: t('common.required-field'),
              },
              {
                type: 'email',
                message: t('signup.invalid-email'),
              },
            ]}
          >
            <FormInput
              width="100%" //@ts-ignore
              border_color="var(--color-border, #dbeae3)"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="timezone"
            label={t('common.timezone')}
            rules={[
              {
                required: true,
                message: t('common.required-field'),
              },
            ]}
          >
            <FormSelect //@ts-ignore
              width="100%"
              bordercolor="var(--color-border, #dbeae3)"
              height="45px"
            >
              {timezones &&
                timezones.map((tz, idx) => (
                  <Select.Option key={idx} value={tz.displayTimeZoneName}>
                    {tz.displayName}
                  </Select.Option>
                ))}
            </FormSelect>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item>
            <ScopeButton
              type="primary"
              htmlType="submit"
              width="100%"
              height="40px"
            >
              {t('organisation.save-details')}
            </ScopeButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
